import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import './index.scss';
import NotFound from '$/pages/not-found';
import { CloService, ClientAnalyticsService } from '$/services';
import { CLIENT_CLO_VIEW } from '$/core/constants';
import AnalyticsHeaderFilter from '$/components/analyticsHeaderFilter';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { colorCode, leverageColorCode } from '$/pages/client/clo-analytics/heatMap';

interface ICloIndustriesProps {
  isLoading: boolean;
  featureFlags?: string[];
  currentClo?: Bixby.Models.Clo;
  heatMapActive: boolean;
}

const CloIndustries: React.FC<ICloIndustriesProps> = props => {
  const { featureFlags, currentClo, heatMapActive } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [dataForGrid, setDataForGrid] = useState([] as Bixby.Models.CloAnalytics[]);
  const [dataError, setDataError] = useState(undefined as string | undefined);
  const [dataRetrievalComplete, setDataRetrievalComplete] = useState(false as boolean);
  const [topQuartileData, setTopQuartileData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);
  const [medianData, setMedianData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);
  const [bottomQuartileData, setBottomQuartileData] = useState(undefined as Bixby.Models.CompanyAnalytics | undefined);

  const analyticsMetrics: Bixby.Enums.AnalyticsMetrics[] = Object.values(Bixby.Enums.AnalyticsMetrics);

  useEffect(() => {
    async function getAnalytics() {
      setIsLoading(true);
      try {
        if (currentClo) {
          const result: Bixby.Models.CloAnalytics[] = await CloService.getCloAnalyticsById(currentClo.id);
          setDataForGrid(result || []);
        }
      } catch (e: any) {
        setDataError('Error retrieving analytics');
      } finally {
        setIsLoading(false);
        setDataRetrievalComplete(true);
      }
    }

    if (currentClo && featureFlags?.includes(CLIENT_CLO_VIEW)) {
      getAnalytics();
    }
  }, [currentClo, featureFlags]);

  useEffect(() => {
    async function getMarketAnalytics() {
      try {
        const result: Bixby.Models.CompanyAnalyticsResult = await ClientAnalyticsService.getMarketDataAnalytics();
        const top = result?.allCompanies.find(x => x.name[1] === 'Top Quartile');
        const median = result?.allCompanies.find(x => x.name[1] === 'Median');
        const bottom = result?.allCompanies.find(x => x.name[1] === 'Bottom Quartile');
        setTopQuartileData(top);
        setMedianData(median);
        setBottomQuartileData(bottom);
      } catch (e: any) {
        setDataError('Error retrieving analytics');
      }
    }

    if (featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
      getMarketAnalytics();
    }
  }, [featureFlags]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const valueFormatter = (params: any, formatter: (value: any) => string, fallback: string) => {
    if (params.value !== null && params.value !== undefined) {
      return formatter(params.value);
    } else if (!params.node.allChildrenCount) {
      return fallback;
    }
    return '';
  };

  class AnalyticsIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      let value: string = params.value;

      const isArray = Bixby.Utils.isArray(params.value);
      if (isArray) {
        value = params.value[params.value.length - 1];
      }
      newDiv.innerHTML = `<span>${value}</span>`;
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  const formatCurrency = (value: number, fallback: string) => {
    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return fallback;
    }

    // add in appropriate formatting
    let prefix = '$';
    let suffix = '';
    if (parsedValue < 0) {
      prefix = prefix + '(';
      suffix = suffix + ')';
    }

    // remove hyphen
    const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

    const currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,').split('.')[0];

    return prefix + currency + suffix;
  };

  const formatMultiplier = (value: number) => {
    const percentage = value.toFixed(1);
    return `${percentage}x`;
  };

  const formatPercentage = (value: number, fractionDigits?: number | undefined) => {
    const percentage = (value * 100).toFixed(fractionDigits || 0);
    return `${percentage}%`;
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getRowHeight = (params: any) => {
    if (analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)) {
      return 20;
    }
    return 32;
  };

  if (!featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
    return <NotFound />;
  }

  if (!Bixby.Utils.isNullOrEmpty(dataError)) {
    return (
      <React.Fragment>
        <section className="analytics-section">
          <div className="analytics-error-section">
            <p className="error">{dataError}</p>
          </div>
        </section>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section className="analytics-section clo_industries">
          {isLoading && <Loading pageLevel />}
          {dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid) ? (
            <div className="clo_industries_grid analytics-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    defaultColDef={{
                      flex: 1,
                      minWidth: 150,
                      resizable: true,
                      lockPinned: true,
                      filter: 'agNumberColumnFilter',
                      cellClass: 'align-center',
                    }}
                    onGridReady={onGridReady}
                    rowData={dataForGrid}
                    suppressDragLeaveHidesColumns={true}
                    suppressClipboardPaste={true}
                    suppressMovableColumns={true}
                    getContextMenuItems={getContextMenuItems}
                    treeData={true}
                    animateRows={true}
                    groupDefaultExpanded={-1}
                    groupDisplayType="custom"
                    getDataPath={function (data) {
                      return data.name;
                    }}
                    rowClassRules={{
                      metric: params => {
                        return analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics);
                      },
                    }}
                    getRowHeight={getRowHeight}
                    headerHeight={32}
                    components={{
                      headerFilter: AnalyticsHeaderFilter,
                      AnalyticsIdentifierCellRenderer: AnalyticsIdentifierCellRenderer,
                    }}
                    columnDefs={[
                      {
                        headerGroupComponent: 'headerFilter',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'name',
                            cellRenderer: 'agGroupCellRenderer',
                            valueFormatter: params => params.node?.key || '',
                            cellClassRules: {
                              sector: params => {
                                return (
                                  params.node.level === 0 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                              'industry-group': params => {
                                return (
                                  params.node.level === 1 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                            },
                            headerClass: 'locked-header-note',
                            headerComponentParams: {
                              template:
                                '<div class="analytics-name"><span class="sector">Sector</span> | <span class="industry-group">Industry Group</span></div>',
                            },
                            minWidth: 350,
                            cellRendererParams: { suppressCount: true, innerRenderer: AnalyticsIdentifierCellRenderer },
                            pinned: 'left',
                            showRowGroup: true,
                          },
                        ],
                      },
                      {
                        headerName: 'EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return leverageColorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.firstLienDebtGrossEbitda,
                                  medianData?.firstLienDebtGrossEbitda,
                                  bottomQuartileData?.firstLienDebtGrossEbitda
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'totalDebtGrossEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return leverageColorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalDebtGrossEbitda,
                                  medianData?.totalDebtGrossEbitda,
                                  bottomQuartileData?.totalDebtGrossEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Adj. EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossAdjustedEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return leverageColorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.firstLienDebtGrossAdjustedEbitda,
                                  medianData?.firstLienDebtGrossAdjustedEbitda,
                                  bottomQuartileData?.firstLienDebtGrossAdjustedEbitda
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'totalDebtGrossAdjustedEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return leverageColorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalDebtGrossAdjustedEbitda,
                                  medianData?.totalDebtGrossAdjustedEbitda,
                                  bottomQuartileData?.totalDebtGrossAdjustedEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Interest Coverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageEbitda',
                            headerName: 'EBITDA',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.interestCoverageEbitda,
                                  medianData?.interestCoverageEbitda,
                                  bottomQuartileData?.interestCoverageEbitda
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'interestCoverageAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.interestCoverageAdjustedEbitda,
                                  medianData?.interestCoverageAdjustedEbitda,
                                  bottomQuartileData?.interestCoverageAdjustedEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Interest Coverage (After Capex)',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageAfterCapitalExpendituresEbitda',
                            headerName: 'EBITDA',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.interestCoverageAfterCapitalExpendituresEbitda,
                                  medianData?.interestCoverageAfterCapitalExpendituresEbitda,
                                  bottomQuartileData?.interestCoverageAfterCapitalExpendituresEbitda
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'interestCoverageAfterCapitalExpendituresAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                                  medianData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                                  bottomQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        field: 'liquidity',
                        headerName: 'Liquidity (1)',
                        minWidth: 130,
                        valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                        cellClass: (params: any) => {
                          if (topQuartileData && medianData && bottomQuartileData) {
                            return colorCode(
                              heatMapActive,
                              params,
                              topQuartileData?.liquidity,
                              medianData?.liquidity,
                              bottomQuartileData?.liquidity
                            );
                          } else return '';
                        },
                      },
                      {
                        headerName: 'MRQ Growth %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'revenueGrowth',
                            headerName: 'Revenue',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.revenueGrowth,
                                  medianData?.revenueGrowth,
                                  bottomQuartileData?.revenueGrowth
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'ebitdaGrowth',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.ebitdaGrowth,
                                  medianData?.ebitdaGrowth,
                                  bottomQuartileData?.ebitdaGrowth
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'adjustedEbitdaGrowth',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.adjustedEbitdaGrowth,
                                  medianData?.adjustedEbitdaGrowth,
                                  bottomQuartileData?.adjustedEbitdaGrowth
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Margin %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'ebitdaMargin',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.ebitdaMargin,
                                  medianData?.ebitdaMargin,
                                  bottomQuartileData?.ebitdaMargin
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'adjustedEbitdaMargin',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.adjustedEbitdaMargin,
                                  medianData?.adjustedEbitdaMargin,
                                  bottomQuartileData?.adjustedEbitdaMargin
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'ocfMargin',
                            headerName: 'OCF',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.ocfMargin,
                                  medianData?.ocfMargin,
                                  bottomQuartileData?.ocfMargin
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'capitalExpendituresMargin',
                            headerName: 'OCF-Capex',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.capitalExpendituresMargin,
                                  medianData?.capitalExpendituresMargin,
                                  bottomQuartileData?.capitalExpendituresMargin
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: '1st Lien Loan Details',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLien1Amount',
                            headerName: '$ Amt.',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.firstLien1Amount,
                                  medianData?.firstLien1Amount,
                                  bottomQuartileData?.firstLien1Amount
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'firstLien1Spread',
                            headerName: 'Spread',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(
                                params,
                                (value: any) => {
                                  return value;
                                },
                                'NM'
                              ),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.firstLien1Spread,
                                  medianData?.firstLien1Spread,
                                  bottomQuartileData?.firstLien1Spread
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        field: 'totalEnterpriseValue',
                        headerName: 'TEV',
                        minWidth: 100,
                        valueFormatter: params =>
                          valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                        cellClass: (params: any) => {
                          if (topQuartileData && medianData && bottomQuartileData) {
                            return colorCode(
                              heatMapActive,
                              params,
                              topQuartileData?.totalEnterpriseValue,
                              medianData?.totalEnterpriseValue,
                              bottomQuartileData?.totalEnterpriseValue
                            );
                          } else return '';
                        },
                      },
                      {
                        headerName: 'Current Loan-to-Value (2)',
                        children: [
                          {
                            field: 'firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: '1st Lien',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                  medianData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                  bottomQuartileData?.firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Total',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                  medianData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda,
                                  bottomQuartileData?.totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'TEV /',
                        children: [
                          {
                            field: 'totalEnterpriseValueByRevenue',
                            headerName: 'Revenue',
                            minWidth: 120,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalEnterpriseValueByRevenue,
                                  medianData?.totalEnterpriseValueByRevenue,
                                  bottomQuartileData?.totalEnterpriseValueByRevenue
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'totalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 120,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda,
                                  medianData?.totalEnterpriseValueByAdjustedManagementEbitda,
                                  bottomQuartileData?.totalEnterpriseValueByAdjustedManagementEbitda
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Financials at transaction',
                        children: [
                          {
                            field: 'revenueAtBuyout',
                            headerName: 'Revenue',
                            minWidth: 130,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.revenueAtBuyout,
                                  medianData?.revenueAtBuyout,
                                  bottomQuartileData?.revenueAtBuyout
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'managementAdjustedEbitdaAtBuyout',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.managementAdjustedEbitdaAtBuyout,
                                  medianData?.managementAdjustedEbitdaAtBuyout,
                                  bottomQuartileData?.managementAdjustedEbitdaAtBuyout
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Capital Structure at transaction',
                        children: [
                          {
                            field: 'netDebtPercentageOfCap',
                            headerName: 'Net Debt % of Cap',
                            minWidth: 200,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.netDebtPercentageOfCap,
                                  medianData?.netDebtPercentageOfCap,
                                  bottomQuartileData?.netDebtPercentageOfCap
                                );
                              } else return '';
                            },
                          },
                          {
                            field: 'totalEquityPercentageOfCap',
                            headerName: 'Total Equity % of Cap',
                            minWidth: 200,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.totalEquityPercentageOfCap,
                                  medianData?.totalEquityPercentageOfCap,
                                  bottomQuartileData?.totalEquityPercentageOfCap
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                      {
                        headerName: 'Change since transaction',
                        children: [
                          {
                            field: 'cumulativeDividendsByImpliedEquityAtRisk',
                            headerName: 'Dividends / Equity "at risk" (3)',
                            minWidth: 250,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            cellClass: (params: any) => {
                              if (topQuartileData && medianData && bottomQuartileData) {
                                return colorCode(
                                  heatMapActive,
                                  params,
                                  topQuartileData?.cumulativeDividendsByImpliedEquityAtRisk,
                                  medianData?.cumulativeDividendsByImpliedEquityAtRisk,
                                  bottomQuartileData?.cumulativeDividendsByImpliedEquityAtRisk
                                );
                              } else return '';
                            },
                          },
                        ],
                      },
                    ]}></AgGridReact>
                </div>
              </div>
              <p className="analytics-note">(1) Calculated as Cash + Available Line of Credit divided by Total Debt.</p>
              <p className="analytics-note">
                (2) Calculated as current 1st Lien (Total) Net Leverage divided by TEV / Adj. EBITDA at the time of
                transaction.
              </p>
              <p className="analytics-note">
                (3) A negative Dividends as % of initial equity contribution ratio reflects subsequent net equity
                contributions.
              </p>
              <p className="analytics-note">Source: CLO holdings provided in partnership with Fitch Group.</p>
            </div>
          ) : dataRetrievalComplete ? (
            <div className="analytics-not-found-section">
              <NoDataFound dataType="analytics" />
            </div>
          ) : null}
        </section>
      </React.Fragment>
    );
  }
};
const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.cloState.isLoading,
    currentClo: state.cloState.currentClo,
    featureFlags: state.loginState.featureFlags,
    heatMapActive: state.cloState.heatMapActive,
  };
};

export default connect(mapStateToProps)(CloIndustries);
